import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TextField,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useDispatch, useSelector } from "react-redux";
import { fetchSupWiseOT } from "../../actions/SupervisiorWiseOTAction";
import { fetchLocationNotOff } from "../../actions/LocationNotOffAction";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";


const PageContainer = styled(Box)(() => ({
  maxWidth: "900px",
  margin: "0 auto",
  padding: "10px 15px",
  fontFamily: "'Poppins', sans-serif",
}));

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: "16px",
  boxShadow: "0px 6px 15px rgba(0,0,0,0.12)",
  border: "1px solid #d6dce5",
  backgroundColor: "#ffffff",
  maxHeight: "650px",
  overflowY: "auto",
  overflowX: "auto",
  position: "relative",
  "& .MuiTable-root": {
    borderCollapse: "separate",
  },
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
  "@media (max-width: 600px)": {
    width: "100%",
    display: "block",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  padding: "14px 10px",
  fontSize: "14px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154C79",
    color: "#FFFFFF",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 10,
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "0.25s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "rgba(243, 246, 250, 0.6)",
  },
  "&:hover": {
    backgroundColor: "rgba(14, 60, 102, 0.09)",
    cursor: "pointer",
  },
}));

export default function SupervisorWiseOT() {
  const dispatch = useDispatch();
  const { loading: supLoading, data: supData } = useSelector((state) => state.supervisorWiseOT);
  const { loading: locLoading, data: locData } = useSelector((state) => state.locationNotOff);

  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState("supervisor"); // supervisor or location

  const formatDate = (dateObj) => {
    const d = new Date(dateObj);
    return d.toISOString().split("T")[0];
  };

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today); 
    const formattedToday = formatDate(today);
    
    if (viewMode === "supervisor") {
      dispatch(fetchSupWiseOT(formattedToday));
    } else {
      dispatch(fetchLocationNotOff(formattedToday));
    }
  }, [dispatch, viewMode]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setOpenDatePicker(false);
    const formatted = formatDate(date);
    if (viewMode === "supervisor") {
      dispatch(fetchSupWiseOT(formatted));
    } else {
      dispatch(fetchLocationNotOff(formatted));
    }
  };

  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
  };

  useEffect(() => {
    const today = new Date();
    setSelectedDate(today);
  }, [viewMode]);

  const currentData = viewMode === "supervisor" ? supData : locData;
  const loading = viewMode === "supervisor" ? supLoading : locLoading;

  // ================= PDF EXPORT =================
  const handleExportPDF = () => {
    const doc = new jsPDF("l", "mm", "a4");

    doc.setFontSize(16);
    doc.text("OT Allocated Employees", 14, 15);

    doc.setFontSize(11);
    doc.text(`Date: ${formatDate(selectedDate)}`, 14, 22);
    doc.text(`View Type: ${viewMode === "supervisor" ? "Based on Supervisor" : "Based on Location"}`, 14, 28);

    let columns = [];
    let rows = [];

    if (viewMode === "supervisor") {
      columns = ["Service No", "Supervisor Name", "Total Assign", "Not Left"];
      rows = currentData?.map(item => [
        item.Sup_serviceNo || "",
        item.Sup_name || "",
        item.Sup_totalCount || "",
        item.Sup_empCount || ""
      ]);
    } else {
      columns = ["Location", "Total Assign", "Not Left"];
      rows = currentData?.map(item => [
        item.Loc_location || "",
        item.Loc_totalCount || "",
        item.Loc_empCount || ""
      ]);
    }

    autoTable(doc, {
      head: [columns],
      body: rows,
      startY: 35,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [21, 76, 121], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 248, 252] },
    });

    doc.save(`OT_Allocated_${formatDate(selectedDate)}.pdf`);
  };
  // =================================================

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <PageContainer>
        {/* Title */}
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: "#0E3C66",
            textAlign: "center",
            mb: 2,
            letterSpacing: "1px",
          }}
        >
          OT Allocated Employees
        </Typography>

        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          {/* Date Select Button */}
          <Grid item xs={6}>
            <Button
              variant="contained"
              startIcon={<CalendarTodayIcon />}
              onClick={() => setOpenDatePicker(true)}
              sx={{
                width: "100%",
                height: "56px",
                borderRadius: "20px",
                backgroundColor: "#142c54",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                fontSize: "16px",
                "&:hover": { backgroundColor: "#0D47A1" },
              }}
            >
              {formatDate(selectedDate)}
            </Button>
          </Grid>

          {/* View Mode Dropdown */}
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, color: "#154C79" }}>
                View Type
              </InputLabel>
              <Select
                value={viewMode}
                onChange={handleViewModeChange}
                label="View Type"
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  borderRadius: "20px",
                  backgroundColor: "#F0F4F8",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  "& .MuiSelect-icon": { color: "#154C79" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#154C79" },
                }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 250, overflowY: "auto" } },
                }}
              >
                <MenuItem value="supervisor">Based on Supervisor</MenuItem>
                <MenuItem value="location">Based on Location</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Date Picker */}
        {openDatePicker && (
          <DatePicker
            open={openDatePicker}
            value={selectedDate}
            onChange={handleDateChange}
            onClose={() => setOpenDatePicker(false)}
            renderInput={(params) => <TextField {...params} sx={{ display: "none" }} />}
          />
        )}

        {/* Total Employee Count */}
        {!loading && currentData?.length > 0 && (() => {
          const countKey = viewMode === "supervisor" ? "Sup_totalCount" : "Loc_totalCount";
          const empCountKey = viewMode === "supervisor" ? "Sup_empCount" : "Loc_empCount";
          const assignCount = currentData.reduce((sum, item) => sum + Number(item[countKey] || 0), 0);
          const notLeftCount = currentData.reduce((sum, item) => sum + Number(item[empCountKey] || 0), 0);

          return (
            <Box sx={{ display: "flex", justifyContent: "center", gap: "20px", mb: 2 }}>
              <Box sx={{ background: "#e8f0fa", border: "1px solid #bbd0ea", padding: "8px 14px", borderRadius: "8px", textAlign: "center", fontWeight: 600, color: "#0E3C66" }}>
                Assigned Count: {assignCount}
              </Box>
              <Box sx={{ background: "#e8f0fa", border: "1px solid #bbd0ea", padding: "8px 14px", borderRadius: "8px", textAlign: "center", fontWeight: 600, color: "#0E3C66" }}>
                Not Left Count: {notLeftCount}
              </Box>
            </Box>
          );
        })()}

        {/* Export PDF Button */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <Button
                  onClick={handleExportPDF}
                  variant="contained"
                  sx={{
                    width: "100%",
                    height: "48px",
                    borderRadius: "18px",
                    backgroundColor: "#2e7d32",
                    fontWeight: "bold",
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#1b5e20" },
                  }}
                >
                  Export Table as PDF
                </Button>
        </Box>

        {/* Table */}
        <StyledTableContainer component={Paper}>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow>
                {viewMode === "supervisor" ? (
                  <>
                    <StyledTableCell sx={{ textAlign: "center" }}>Service No</StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "left" }}>Supervisor Name</StyledTableCell>
                  </>
                ) : (
                  <StyledTableCell sx={{ textAlign: "center" }}>Location</StyledTableCell>
                )}
                <StyledTableCell sx={{ textAlign: "center" }}>Total Assign</StyledTableCell>
                <StyledTableCell sx={{ textAlign: "center" }}>Not Left</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading &&
                currentData?.map((item, index) => (
                  <StyledTableRow key={index}>
                    {viewMode === "supervisor" ? (
                      <>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Sup_serviceNo}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "left" }}>{item.Sup_name}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Sup_totalCount}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Sup_empCount}</StyledTableCell>
                      </>
                    ) : (
                      <>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Loc_location}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Loc_totalCount}</StyledTableCell>
                        <StyledTableCell sx={{ textAlign: "center" }}>{item.Loc_empCount}</StyledTableCell>
                      </>
                    )}
                  </StyledTableRow>
                ))}

              {loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </PageContainer>
    </LocalizationProvider>
  );
}
